/**
 * Deutsche Feiertage (bundesweit + bundeslandspezifisch).
 * Berechnung des Osterdatums via Gauß-Formel, daraus alle beweglichen Feiertage.
 *
 * Nutzung:
 *   import { getHolidaysForYear, getHoliday } from "@/utils/holidays";
 *   const map = getHolidaysForYear(2026, "DE-BY");  // Map<"yyyy-MM-dd", {name, state}>
 *   const h = getHoliday("2026-05-14");  // -> {name: "Christi Himmelfahrt", states:["DE"], bundeseinheitlich:true}
 */

/** Osterdatum (Gregorianisch) via Gauß-Formel */
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDaysISO(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const STATE_LIST = ["BW", "BY", "BE", "BB", "HB", "HH", "HE", "MV", "NI", "NW", "RP", "SL", "SN", "ST", "SH", "TH"];

/**
 * @param {number} year
 * @param {string} state - Optional Bundesland-Code (DE-BY) – wenn nicht angegeben, nur bundesweite Feiertage.
 * @returns {Map<string, {name: string, states: string[], bundeseinheitlich: boolean}>}
 */
export function getHolidaysForYear(year, state = null) {
  const easter = easterSunday(year);
  const holidays = [];

  // ── Bundesweit ──
  holidays.push({ date: `${year}-01-01`, name: "Neujahr", states: STATE_LIST, bundeseinheitlich: true });
  holidays.push({ date: fmt(addDaysISO(easter, -2)), name: "Karfreitag", states: STATE_LIST, bundeseinheitlich: true });
  holidays.push({ date: fmt(addDaysISO(easter, 1)), name: "Ostermontag", states: STATE_LIST, bundeseinheitlich: true });
  holidays.push({ date: `${year}-05-01`, name: "Tag der Arbeit", states: STATE_LIST, bundeseinheitlich: true });
  holidays.push({ date: fmt(addDaysISO(easter, 39)), name: "Christi Himmelfahrt", states: STATE_LIST, bundeseinheitlich: true });
  holidays.push({ date: fmt(addDaysISO(easter, 50)), name: "Pfingstmontag", states: STATE_LIST, bundeseinheitlich: true });
  holidays.push({ date: `${year}-10-03`, name: "Tag der Deutschen Einheit", states: STATE_LIST, bundeseinheitlich: true });
  holidays.push({ date: `${year}-12-25`, name: "1. Weihnachtstag", states: STATE_LIST, bundeseinheitlich: true });
  holidays.push({ date: `${year}-12-26`, name: "2. Weihnachtstag", states: STATE_LIST, bundeseinheitlich: true });

  // ── Bundeslandspezifisch ──
  // Heilige Drei Könige (6.1) — BW, BY, ST
  holidays.push({ date: `${year}-01-06`, name: "Heilige Drei Könige", states: ["BW", "BY", "ST"], bundeseinheitlich: false });
  // Internationaler Frauentag (8.3) — BE, MV
  holidays.push({ date: `${year}-03-08`, name: "Internationaler Frauentag", states: ["BE", "MV"], bundeseinheitlich: false });
  // Fronleichnam (Easter + 60) — BW, BY, HE, NW, RP, SL + Teile von SN/TH
  holidays.push({ date: fmt(addDaysISO(easter, 60)), name: "Fronleichnam", states: ["BW", "BY", "HE", "NW", "RP", "SL", "SN", "TH"], bundeseinheitlich: false });
  // Augsburger Friedensfest (8.8) — Augsburg only
  holidays.push({ date: `${year}-08-08`, name: "Augsburger Friedensfest", states: ["BY"], bundeseinheitlich: false });
  // Mariä Himmelfahrt (15.8) — BY (kath.), SL
  holidays.push({ date: `${year}-08-15`, name: "Mariä Himmelfahrt", states: ["BY", "SL"], bundeseinheitlich: false });
  // Weltkindertag (20.9) — TH
  holidays.push({ date: `${year}-09-20`, name: "Weltkindertag", states: ["TH"], bundeseinheitlich: false });
  // Reformationstag (31.10) — BB, HB, HH, MV, NI, SN, ST, SH, TH
  holidays.push({ date: `${year}-10-31`, name: "Reformationstag", states: ["BB", "HB", "HH", "MV", "NI", "SN", "ST", "SH", "TH"], bundeseinheitlich: false });
  // Allerheiligen (1.11) — BW, BY, NW, RP, SL
  holidays.push({ date: `${year}-11-01`, name: "Allerheiligen", states: ["BW", "BY", "NW", "RP", "SL"], bundeseinheitlich: false });
  // Buß- und Bettag (Mittwoch vor 23.11) — SN
  const bbDate = new Date(year, 10, 22);
  while (bbDate.getDay() !== 3) bbDate.setDate(bbDate.getDate() - 1);
  holidays.push({ date: fmt(bbDate), name: "Buß- und Bettag", states: ["SN"], bundeseinheitlich: false });

  // ── Map bauen, nach state filtern ──
  const map = new Map();
  for (const h of holidays) {
    if (state && !h.bundeseinheitlich) {
      const code = state.startsWith("DE-") ? state.slice(3) : state;
      if (!h.states.includes(code)) continue;
    }
    if (state === null && !h.bundeseinheitlich) continue; // Nur bundesweit anzeigen wenn kein State angegeben? Nein, default = ALLE bundesweite
    if (!map.has(h.date)) map.set(h.date, { name: h.name, states: h.states, bundeseinheitlich: h.bundeseinheitlich });
  }
  return map;
}

/** Gibt zurück, ob ein bestimmtes Datum ein Feiertag ist (bundesweit). */
let _cache = new Map();
export function getHoliday(dateStr, state = null) {
  const y = Number(dateStr.slice(0, 4));
  const cacheKey = `${y}::${state || ""}`;
  if (!_cache.has(cacheKey)) {
    _cache.set(cacheKey, getHolidaysForYear(y, state));
  }
  return _cache.get(cacheKey).get(dateStr) || null;
}

export function isHoliday(dateStr, state = null) {
  return getHoliday(dateStr, state) !== null;
}
